import * as React from "react"
import { useTranslation } from "react-i18next"
import { Check, ChevronsUpDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { SelectedLabels } from "@/components/shared/selected-labels"

import { Button } from "./button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import {
  Control,
  FieldPathValue,
  FieldValues,
  Path,
  useController,
} from "react-hook-form"
import { type PopoverContentProps } from "@radix-ui/react-popover"
export interface SelectOption<TValue = string> {
  label: string
  value: TValue
  group?: string
}

export function ComboboxEmpty({
  emptyMessage,
  children,
  ...props
}: React.CustomComponentPropsWithRef<typeof CommandEmpty> & {
  emptyMessage?: string
}) {
  const { t } = useTranslation("common")
  return (
    <CommandEmpty {...props}>
      <p className="text-gray-400">
        {emptyMessage || t("combobox.default_empty_message")}
      </p>
      {children}
    </CommandEmpty>
  )
}

export function ComboboxTrigger({
  open,
  ...props
}: React.CustomComponentPropsWithRef<typeof PopoverTrigger> & {
  open: boolean
}) {
  return <PopoverTrigger role="combobox" {...props} aria-expanded={open} />
}

type ComboboxPopoverContentProps = Omit<
  PopoverContentProps,
  keyof React.HTMLAttributes<HTMLDivElement>
>

interface ComboboxPrimitiveProps<
  TValue extends React.Key | null | undefined,
  TOption extends SelectOption<TValue>,
> extends ComboboxPopoverContentProps {
  options: Array<TOption>
  isSearchable?: boolean
  searchPlaceholder?: string
  className?: string
}

interface ComboboxPrimitiveRender<
  TValue extends React.Key | null | undefined,
  TOption extends SelectOption<TValue>,
> {
  renderComboboxTrigger: ({ open }: { open: boolean }) => React.ReactNode
  renderComboboxItem: ({
    option,
    key,
    setOpen,
  }: {
    option: TOption
    key: string
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
  }) => React.ReactNode
  renderComboboxEmpty: () => React.ReactNode
}

function ComboboxPrimitive<
  TValue extends React.Key | null | undefined,
  TOption extends SelectOption<TValue>,
>({
  renderComboboxTrigger,
  renderComboboxItem,
  renderComboboxEmpty,
  options,
  isSearchable,
  searchPlaceholder,
  className,
  ...props
}: ComboboxPrimitiveProps<TValue, TOption> &
  ComboboxPrimitiveRender<TValue, TOption>) {
  const { t } = useTranslation(["common"])
  const [open, setOpen] = React.useState(false)

  const groupedOptions = React.useMemo(
    () => Object.groupBy(options, (option) => option.group!),
    [options],
  )

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      {renderComboboxTrigger({ open })}
      <PopoverContent
        className="pointer-events-auto w-[var(--radix-popover-trigger-width)] p-0"
        {...props}
      >
        <Command className={className}>
          {isSearchable ? (
            <CommandInput
              placeholder={
                searchPlaceholder ||
                t("common:combobox.default_search_placeholder")
              }
            />
          ) : null}
          <CommandList>
            {Object.keys(groupedOptions).map((key) => (
              <CommandGroup heading={key !== "undefined" && key} key={key}>
                {groupedOptions[key]?.map((option) =>
                  renderComboboxItem({ option, key, setOpen }),
                )}
              </CommandGroup>
            ))}
          </CommandList>
          {renderComboboxEmpty()}
        </Command>
      </PopoverContent>
    </Popover>
  )
}

interface ComboboxProps<
  TValue extends React.Key | null | undefined,
  TOption extends SelectOption<TValue>,
>
  extends
    ComboboxPrimitiveProps<TValue, TOption>,
    Partial<ComboboxPrimitiveRender<TValue, TOption>> {
  id?: string
  selectPlaceholder?: string
  ariaLabelledBy?: string
  ariaLabel?: string
  emptyMessage?: string
}

function defaultRenderEmpty(emptyMessage?: string) {
  return () => <ComboboxEmpty emptyMessage={emptyMessage} />
}

export function Combobox<
  TValue extends React.Key | null | undefined,
  TOption extends SelectOption<TValue>,
>({
  id,
  selectPlaceholder,
  searchPlaceholder,
  emptyMessage,
  onChange = () => {},
  options = [],
  value,
  className,
  ariaLabelledBy,
  ariaLabel,
  isSearchable = true,
  renderComboboxTrigger,
  renderComboboxItem,
  renderComboboxEmpty = defaultRenderEmpty(emptyMessage),
  ...props
}: ComboboxProps<TValue, TOption> & {
  onChange?: (value: TValue | null) => void
  value?: TValue | null
}) {
  const { t } = useTranslation(["common"])
  const label = options.find((option) => option.value === value)?.label

  const defaultRenderTrigger = React.useCallback(
    ({ open }: { open: boolean }) => {
      return (
        <ComboboxTrigger asChild open={open}>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            id={id}
            aria-labelledby={ariaLabelledBy}
            aria-label={ariaLabel}
          >
            {label ||
              selectPlaceholder ||
              t("common:combobox.default_select_placeholder")}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </ComboboxTrigger>
      )
    },
    [label, selectPlaceholder, t, id, ariaLabelledBy, ariaLabel],
  )

  const defaultRenderItem = React.useCallback(
    ({
      option,
      key,
      setOpen,
    }: {
      option: SelectOption<TValue>
      key: string
      setOpen: React.Dispatch<React.SetStateAction<boolean>>
    }) => (
      <CommandItem
        key={option.value}
        keywords={[key, option.value?.toString() ?? ""]}
        onSelect={() => {
          if (value === option.value) {
            onChange(null)
          } else {
            onChange(option.value)
          }
          setOpen(false)
        }}
      >
        {option.label}
      </CommandItem>
    ),
    [value, onChange],
  )

  return (
    <ComboboxPrimitive
      {...props}
      options={options}
      className={className}
      isSearchable={isSearchable}
      searchPlaceholder={searchPlaceholder}
      renderComboboxEmpty={renderComboboxEmpty}
      renderComboboxTrigger={renderComboboxTrigger || defaultRenderTrigger}
      renderComboboxItem={renderComboboxItem || defaultRenderItem}
    />
  )
}

export function MultiCombobox<
  TValue extends React.Key | null | undefined,
  TOption extends SelectOption<TValue>,
>({
  id,
  selectPlaceholder,
  searchPlaceholder,
  emptyMessage,
  options = [],
  onChange,
  selected,
  className,
  ariaLabelledBy,
  ariaLabel,
  isSearchable = true,
  renderComboboxTrigger,
  renderComboboxItem,
  renderComboboxEmpty = defaultRenderEmpty(emptyMessage),
  ...props
}: ComboboxProps<TValue, TOption> & {
  onChange: (selected: SelectOption<TValue>[]) => void
  selected: SelectOption<TValue>[]
}) {
  const { t } = useTranslation(["common"])

  const handleSelect = React.useCallback(
    (value: SelectOption<TValue>, setOpen: (open: boolean) => void) => {
      const updatedSelected = selected.some(
        (item) => item.value === value.value,
      )
        ? selected.filter((item) => item.value !== value.value)
        : [...selected, value]
      onChange(updatedSelected)
      setOpen(false)
    },
    [selected, onChange],
  )

  const defaultRenderTrigger = React.useCallback(
    ({ open }: { open: boolean }) => (
      <ComboboxTrigger asChild open={open}>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          id={id}
          className="h-min w-full flex-wrap justify-start"
          aria-labelledby={ariaLabelledBy}
          aria-label={ariaLabel}
        >
          <SelectedLabels selected={selected} />
          <div className="flex grow items-center justify-between">
            {selectPlaceholder ||
              t("common:combobox.default_select_placeholder")}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </ComboboxTrigger>
    ),
    [id, ariaLabelledBy, ariaLabel, selected, selectPlaceholder, t],
  )

  const defaultRenderItem = React.useCallback(
    ({
      option,
      key,
      setOpen,
    }: {
      option: SelectOption<TValue>
      key: string
      setOpen: React.Dispatch<React.SetStateAction<boolean>>
    }) => (
      <CommandItem
        key={option.value}
        onSelect={() => handleSelect(option, setOpen)}
        keywords={[key, option.value?.toString() ?? ""]}
      >
        {option.label}
        <Check
          className={cn(
            "ml-auto h-4 w-4",
            selected.some((item) => item.value === option.value)
              ? "opacity-100"
              : "opacity-0",
          )}
        />
      </CommandItem>
    ),
    [handleSelect, selected],
  )

  return (
    <ComboboxPrimitive
      {...props}
      className={className}
      options={options}
      isSearchable={isSearchable}
      searchPlaceholder={searchPlaceholder}
      renderComboboxEmpty={renderComboboxEmpty}
      renderComboboxTrigger={renderComboboxTrigger || defaultRenderTrigger}
      renderComboboxItem={renderComboboxItem || defaultRenderItem}
    />
  )
}

export function ControlledCombobox<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  name,
  control,
  rules,
  ...props
}: Omit<
  React.ComponentProps<
    typeof Combobox<
      FieldPathValue<TFieldValues, TName>,
      SelectOption<FieldPathValue<TFieldValues, TName>>
    >
  >,
  "value" | "onChange"
> & {
  name?: Path<TFieldValues>
  control?: Control<TFieldValues>
  rules?: object
}) {
  const { field } = useController({ name: name!, control, rules })

  return (
    <Combobox
      {...props}
      value={field.value}
      onChange={(value) => field.onChange(value)}
    />
  )
}

export function ControlledMultiCombobox<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  name,
  control,
  rules,
  ...props
}: Omit<
  React.ComponentProps<
    typeof MultiCombobox<
      FieldPathValue<TFieldValues, TName>,
      SelectOption<FieldPathValue<TFieldValues, TName>>
    >
  >,
  "selected" | "onChange"
> & {
  name?: Path<TFieldValues>
  control?: Control<TFieldValues>
  rules?: object
}) {
  const { field } = useController({ name: name!, control, rules })
  const selected = props.options.filter(
    (option) => field.value && field.value.includes(option.value),
  )

  return (
    <MultiCombobox
      {...props}
      selected={selected}
      onChange={(options) => field.onChange(options.map(({ value }) => value))}
    />
  )
}
